/**
 * Terminal sağlığı — KK-804, KK-409, KK-410, AK-12, GV-02.
 *
 * =============================================================================
 * İKİ AYRI SORU, İKİ AYRI RENK
 * -----------------------------------------------------------------------------
 * · OFFLINE MI (KK-409): son heartbeat 3 dakikadan eski mi. Offline olmak TEK
 *   BAŞINA ARIZA DEĞİLDİR — sistem zaten offline çalışacak şekilde tasarlandı
 *   (NF-13). Bu yüzden offline rozeti UYARI tonundadır, alarm değil.
 *
 * · KUYRUK ALARMI (KK-410): kuyrukta iş VAR ve terminalden 2 saattir sunucuya
 *   hiçbir işlem ULAŞMADI. Asıl tehlike budur: para hareketi cihazda mahsur
 *   kalmıştır. Bu KIRMIZI ALARMDIR ve tablonun başında durur.
 *
 * · DEPOLAMA İZNİ (AK-12): navigator.storage.persist() reddedilmiş terminal,
 *   offline dayanıklılık vaadini karşılayamaz. Tarayıcı depolamayı temizlerse
 *   gönderilmemiş satışlar kaybolur; bu yüzden ayrı bir sayaç ve kalıcı rozet.
 *
 * GV-02: token yenileme TEK TIKTIR ve yeni token YALNIZCA BİR KEZ gösterilir —
 * sunucu yalnızca HMAC özetini saklar, bir daha üretilemez.
 * =============================================================================
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Buton,
  Kart,
  Rozet,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Tutar,
  Uyari,
} from "@kantin/ui";
import { Rol, TERMINAL_KURULUM_ROLLERI } from "@kantin/shared";
import { IstatistikKarosu } from "@kantin/ui/grafik";
import { oturumDurumu, useYetkili } from "../../veri/oturum.js";
import {
  useNoktalar,
  useTerminalSagligi,
  useTokenYenile,
} from "../../veri/sorgular.js";
import { apiYaz } from "../../veri/api.js";
import type { TerminalSaglik, TokenYenilemeYaniti } from "../../veri/tipler.js";
import { TERMINAL_DURUM_ADI, tarihSaat, yaslanma } from "../../veri/bicim.js";
import { IslemDurumu, Sayfa, SayfaBasligi, SorguDurumu } from "../../ortak.js";

export function TerminalSagligi() {
  const seciliNoktaId = oturumDurumu((s) => s.seciliNoktaId);
  const sorgu = useTerminalSagligi();
  const yenile = useTokenYenile();
  const [yeniToken, setYeniToken] = useState<TokenYenilemeYaniti | null>(null);
  const [kurulumKodu, setKurulumKodu] = useState<{
    kod: string;
    sonKullanma: string;
  } | null>(null);
  const [kurulumNoktasi, setKurulumNoktasi] = useState("");
  const [kurulumHatasi, setKurulumHatasi] = useState<string | null>(null);
  const [kodUretiliyor, setKodUretiliyor] = useState(false);
  const noktalar = useNoktalar();
  const kurulumYetkili = useYetkili(...TERMINAL_KURULUM_ROLLERI);
  // Token yenileme sunucuda TANIM_YAZMA ile korunur; nokta görevlisi listeyi
  // görür ama düğmeyi görmez — 403 duvarına davet edilmez.
  const tokenYetkili = useYetkili(
    Rol.KANTIN_MUDURU,
    Rol.OKUL_YONETIMI,
    Rol.OKUL_SISTEM_YONETICISI,
  );
  const noktaKapsami = sorgu.data?.kapsam === "nokta";
  const tokeniKapat = useCallback(() => setYeniToken(null), []);

  const terminaller = useMemo(() => {
    const hepsi = sorgu.data?.terminaller ?? [];
    if (!seciliNoktaId) return hepsi;
    return hepsi.filter((t) => t.nokta_id === seciliNoktaId);
  }, [sorgu.data, seciliNoktaId]);

  const ozet = sorgu.data?.ozet;
  const esikler = sorgu.data?.esikler;

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Terminaller"
        aciklama={
          esikler ? (
            <>
              Heartbeat{" "}
              <span className="rakam font-mono">{esikler.offline_saniye}</span>{" "}
              saniyeden eski ise çevrimdışı sayılır; kuyruğu{" "}
              <span className="rakam font-mono">
                {esikler.kuyruk_alarm_dakika}
              </span>{" "}
              dakikadır boşalmayan terminal kırmızı alarm üretir.
            </>
          ) : (
            "Son heartbeat, kuyruk derinliği, sürümler ve depolama izni"
          )
        }
      />

      {yeniToken && <TokenPenceresi yanit={yeniToken} kapat={tokeniKapat} />}

      {noktaKapsami && (
        <Uyari tur="bilgi">
          Bu liste <strong>atandığınız noktaların</strong> terminallerini
          gösterir; okulun diğer terminalleri kapsam dışıdır.
        </Uyari>
      )}

      {kurulumYetkili && (
        <Kart baslik="Yeni terminal kurulumu">
          <p className="text-sm">
            Satış noktasını seçip kurulum kodu oluşturun. Kodu, kurulacak
            cihazda kasa uygulamasına girin. Kod tek kullanımlıktır ve 30 dakika
            geçerlidir.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-64 flex-col gap-1 text-sm">
              Satış noktası
              <select
                className="h-9 rounded-md border border-cizgi bg-kagit px-3"
                value={kurulumNoktasi}
                onChange={(e) => setKurulumNoktasi(e.target.value)}
              >
                <option value="">Nokta seçin</option>
                {(noktalar.data?.noktalar ?? [])
                  .filter((n) => n.aktif && n.satis_yapar)
                  .map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.ad}
                    </option>
                  ))}
              </select>
            </label>
            <Buton
              disabled={!kurulumNoktasi}
              yukleniyor={kodUretiliyor}
              onClick={() => void kodUret()}
            >
              30 dakikalık kod üret
            </Buton>
          </div>
          {kurulumHatasi && (
            <p className="text-sm text-hata">{kurulumHatasi}</p>
          )}
          {kurulumKodu && (
            <Uyari tur="uyari" baslik="Kurulum kodu — şimdi kopyalayın">
              <p className="m-0 font-mono text-lg font-semibold">
                {kurulumKodu.kod}
              </p>
              <p className="mb-0 text-sm">
                Son kullanım: {tarihSaat(kurulumKodu.sonKullanma)}
              </p>
              <Buton tur="sessiz" onClick={() => setKurulumKodu(null)}>
                Ekrandan kaldır
              </Buton>
            </Uyari>
          )}
        </Kart>
      )}

      {ozet && (
        <section
          aria-label="Terminal özeti"
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        >
          <IstatistikKarosu
            etiket="Tanımlı terminal"
            deger={<span className="rakam font-mono">{ozet.toplam}</span>}
            yardimci="İptal edilmemiş terminaller"
          />
          <IstatistikKarosu
            etiket="Çevrimdışı"
            deger={<span className="rakam font-mono">{ozet.offline}</span>}
            yardimci="Offline olmak tek başına arıza değildir"
          />
          <IstatistikKarosu
            etiket="Kuyruk alarmı"
            vurgu={ozet.kuyruk_alarmi > 0}
            deger={
              <span className="rakam font-mono">{ozet.kuyruk_alarmi}</span>
            }
            yardimci="Para hareketi cihazda mahsur"
          />
          <IstatistikKarosu
            etiket="Depolama izni yok"
            vurgu={ozet.depolama_izni_yok > 0}
            deger={
              <span className="rakam font-mono">{ozet.depolama_izni_yok}</span>
            }
            yardimci="Gönderilmemiş satış kaybolabilir (AK-12)"
          />
        </section>
      )}

      {ozet && ozet.kuyruk_alarmi > 0 && (
        <Uyari tur="hata" baslik="Kuyruğu boşalmayan terminal var">
          Aşağıda kırmızı alarmlı terminaller listenin başındadır. Bu
          cihazlardaki satışlar sunucuya ULAŞMAMIŞTIR; önce ağ bağlantısını,
          sonra kasa uygulamasının açık olup olmadığını kontrol edin.
        </Uyari>
      )}

      <SorguDurumu
        yukleniyor={sorgu.isLoading}
        hata={sorgu.error}
        bos={!sorgu.isLoading && terminaller.length === 0}
        bosBaslik="Terminal yok"
        bosAciklama={
          seciliNoktaId
            ? "Seçili noktada tanımlı terminal bulunmuyor."
            : noktaKapsami
              ? "Atandığınız noktalarda tanımlı terminal bulunmuyor."
              : "Bu okulda tanımlı terminal bulunmuyor."
        }
        iskeletSatir={6}
        yenile={() => void sorgu.refetch()}
      >
        <Tablo>
          <TabloBaslik>
            <th scope="col">Terminal</th>
            <th scope="col">Nokta</th>
            <th scope="col">Durum</th>
            <th scope="col">Son heartbeat</th>
            <th scope="col">Son işlem</th>
            <th scope="col">Kuyruk</th>
            <th scope="col">Uygulama</th>
            <th scope="col">Agent</th>
            <th scope="col">Depolama</th>
            <th scope="col">Offline tavan</th>
            <th scope="col">Token</th>
          </TabloBaslik>
          <TabloGovde>
            {terminaller.map((t) => (
              <Satir
                key={t.id}
                terminal={t}
                yenileniyor={yenile.isPending && yenile.variables === t.id}
                yenileyin={
                  tokenYetkili
                    ? () =>
                        yenile.mutate(t.id, {
                          onSuccess: (y) => setYeniToken(y),
                        })
                    : null
                }
              />
            ))}
          </TabloGovde>
        </Tablo>

        <IslemDurumu hata={yenile.error} />
      </SorguDurumu>
    </Sayfa>
  );

  async function kodUret() {
    setKodUretiliyor(true);
    setKurulumHatasi(null);
    try {
      const sonuc = await apiYaz<{
        kurulum_kodu: string;
        son_kullanma_zamani: string;
      }>("POST", "/terminal-kurulum-kodu", { nokta_id: kurulumNoktasi });
      setKurulumKodu({
        kod: sonuc.kurulum_kodu,
        sonKullanma: sonuc.son_kullanma_zamani,
      });
    } catch (e) {
      setKurulumHatasi((e as Error).message);
    } finally {
      setKodUretiliyor(false);
    }
  }
}

// ---------------------------------------------------------------------------

function Satir({
  terminal,
  yenileniyor,
  yenileyin,
}: {
  terminal: TerminalSaglik;
  yenileniyor: boolean;
  /** null: bu rol token yenileyemez, düğme çizilmez. */
  yenileyin: (() => void) | null;
}) {
  return (
    <tr className={terminal.kuyruk_alarmi ? "bg-hata-yumusak" : undefined}>
      <td>
        <span className="font-semibold text-murekkep">{terminal.ad}</span>
        <span className="rakam ml-2 font-mono text-xs text-murekkep-3">
          #{terminal.id}
        </span>
      </td>
      <td>{terminal.nokta_ad}</td>
      <td>
        <div className="flex flex-wrap gap-1">
          {terminal.kuyruk_alarmi && <Rozet tur="hata">kuyruk alarmı</Rozet>}
          {terminal.offline_mi ? (
            <Rozet tur="uyari">çevrimdışı</Rozet>
          ) : (
            <Rozet tur="onay">çevrimiçi</Rozet>
          )}
          {terminal.durum !== 1 && (
            <Rozet tur="notr">
              {TERMINAL_DURUM_ADI[terminal.durum] ?? `Durum ${terminal.durum}`}
            </Rozet>
          )}
        </div>
      </td>
      <td>
        <span className="rakam font-mono">
          {terminal.son_heartbeat ? yaslanma(terminal.son_heartbeat) : "—"}
        </span>
        <span className="block text-xs text-murekkep-3">
          {tarihSaat(terminal.son_heartbeat)}
        </span>
      </td>
      <td>
        <span className="rakam font-mono">
          {terminal.son_islem_zamani
            ? yaslanma(terminal.son_islem_zamani)
            : "—"}
        </span>
      </td>
      <td className="rakam font-mono">
        {terminal.kuyruk_derinligi}
        {/* Kuyruk 0 olsa bile sayaç görünür kalır (KK-312 ruhu). */}
      </td>
      <td className="rakam font-mono text-xs">
        {terminal.uygulama_surumu ?? "—"}
      </td>
      <td className="rakam font-mono text-xs">
        {terminal.agent_surumu ?? "—"}
      </td>
      <td>
        {terminal.depolama_izni === false ? (
          <Rozet tur="hata">izin yok</Rozet>
        ) : terminal.depolama_izni === true ? (
          <Rozet tur="onay">kalıcı</Rozet>
        ) : (
          <Rozet tur="notr">bilinmiyor</Rozet>
        )}
      </td>
      <td className="text-right">
        <Tutar deger={terminal.offline_tavan} yonsuz boy="sm" />
      </td>
      <td>
        {yenileyin ? (
          <Buton tur="ikincil" yukleniyor={yenileniyor} onClick={yenileyin}>
            Token yenile
          </Buton>
        ) : (
          <span className="text-xs text-murekkep-3">—</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------

/**
 * GV-02: yeni token YALNIZCA BURADA ve YALNIZCA BİR KEZ görünür.
 * Kapatıldığında istemci bellekten de düşer; sunucu zaten yalnızca özetini
 * saklar. Bu yüzden uyarı sus payı olmadan yazılır.
 */
function TokenPenceresi({
  yanit,
  kapat,
}: {
  yanit: TokenYenilemeYaniti;
  kapat: () => void;
}) {
  const [kopyalandi, setKopyalandi] = useState(false);
  const [kopyaHatasi, setKopyaHatasi] = useState(false);

  // Escape ile kapanma: klavye kullanıcısı pencerede kilitli kalmamalıdır.
  useEffect(() => {
    const tus = (o: KeyboardEvent) => {
      if (o.key === "Escape") kapat();
    };
    window.addEventListener("keydown", tus);
    return () => window.removeEventListener("keydown", tus);
  }, [kapat]);

  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(yanit.token);
      setKopyalandi(true);
      setKopyaHatasi(false);
    } catch {
      setKopyaHatasi(true);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="token-baslik"
      className="fixed inset-0 z-20 flex items-center justify-center bg-perde p-4 print:hidden"
    >
      <div className="kip w-full max-w-xl rounded-xl border border-cetvel bg-yuzey-2 p-4 shadow-3">
        <h2 id="token-baslik" className="m-0 text-xl">
          {yanit.ad} · Terminal #{yanit.terminal_id} — yeni terminal tokenı
        </h2>

        <Uyari
          tur="uyari"
          baslik="Bu token bir daha gösterilmeyecek"
          className="mt-3"
        >
          {yanit.token_uyarisi} Önceki token bu andan itibaren geçersizdir;
          kasadaki “Terminal kimliğini onar” ekranına bu tokenı yapıştırıp
          doğrulayın. Terminal kaydı ve yerel satışlar korunur.
        </Uyari>

        <label
          htmlFor="token-degeri"
          className="mt-4 block text-sm font-semibold text-murekkep-2"
        >
          Token
        </label>
        <textarea
          id="token-degeri"
          readOnly
          rows={3}
          value={yanit.token}
          onFocus={(e) => e.currentTarget.select()}
          className="mt-1 w-full break-all rounded-md border border-cetvel bg-kagit-2 p-2 font-mono text-sm text-murekkep"
        />

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Buton tur="birincil" autoFocus onClick={() => void kopyala()}>
            Panoya kopyala
          </Buton>
          <Buton tur="ikincil" onClick={kapat}>
            Kapattım, kaydettim
          </Buton>
          {kopyalandi && (
            <span role="status" className="text-sm text-onay">
              Kopyalandı.
            </span>
          )}
          {kopyaHatasi && (
            <span role="alert" className="text-sm text-hata">
              Pano erişimi yok — token'ı elle seçip kopyalayın.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
